// Staged asset loading. Each stage is a list of entries; stages load in order so the title
// screen can show as soon as the first stage is in, while later stages stream behind it.
import { type Object3D, type Texture, type WebGLRenderer, type Camera, type Scene, LoadingManager, TextureLoader, SRGBColorSpace } from 'three';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { HDRLoader } from 'three/addons/loaders/HDRLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';

export type AssetKind = 'gltf' | 'texture' | 'colorTexture' | 'hdr';

export interface AssetEntry {
  key: string;
  kind: AssetKind;
  url: string; // relative to the site base, e.g. 'assets/cat/cat.glb'
}

export interface AssetStage {
  name: string;
  entries: AssetEntry[];
}

export type AssetProgress = (p: { stage: string; loaded: number; total: number; fraction: number }) => void;

type Loaded = GLTF | Texture;

export class AssetStore {
  private items = new Map<string, Loaded>();
  private manager = new LoadingManager();
  private gltf = new GLTFLoader(this.manager).setMeshoptDecoder(MeshoptDecoder);
  private tex = new TextureLoader(this.manager);
  private hdr = new HDRLoader(this.manager);

  /** Loads the stages in order. Progress covers all entries across all stages. */
  async load(stages: AssetStage[], onProgress?: AssetProgress): Promise<void> {
    const total = stages.reduce((n, s) => n + s.entries.length, 0);
    let loaded = 0;
    for (const stage of stages) {
      await Promise.all(stage.entries.map(async (e) => {
        this.items.set(e.key, await this.loadOne(e));
        loaded++;
        onProgress?.({ stage: stage.name, loaded, total, fraction: total ? loaded / total : 1 });
      }));
    }
    if (total === 0) onProgress?.({ stage: 'done', loaded: 0, total: 0, fraction: 1 });
  }

  private loadOne(e: AssetEntry): Promise<Loaded> {
    const url = import.meta.env.BASE_URL + e.url;
    switch (e.kind) {
      case 'gltf': return this.gltf.loadAsync(url);
      case 'hdr': return this.hdr.loadAsync(url);
      case 'texture': return this.tex.loadAsync(url);
      case 'colorTexture': return this.tex.loadAsync(url).then((t) => { t.colorSpace = SRGBColorSpace; return t; });
    }
  }

  has(key: string): boolean { return this.items.has(key); }

  gltfOf(key: string): GLTF {
    const v = this.items.get(key);
    if (!v || !('scene' in v)) throw new Error(`assets: no glTF "${key}"`);
    return v;
  }

  texture(key: string): Texture {
    const v = this.items.get(key);
    if (!v || 'scene' in v) throw new Error(`assets: no texture "${key}"`);
    return v;
  }
}

/** Compiles shaders up front so the first race frame does not stall. */
export async function precompile(renderer: WebGLRenderer, root: Object3D | Scene, camera: Camera, scene?: Scene): Promise<void> {
  await renderer.compileAsync(root, camera, scene);
}

export const assets = new AssetStore();
